import { useMemo, useState } from "react";
import { mutate } from "swr";
import { useTasks as useTasksAPI, useCreateTask, useUpdateTask, useDeleteTask } from "../api/tasks";
import { TaskService } from "../services/taskService";
export function useTasks(workspaceId, projectId, filters) {
    const { data: tasks, error, isLoading, refetch } = useTasksAPI(workspaceId, projectId, filters);
    const metrics = useMemo(() => {
        if (!tasks)
            return null;
        return TaskService.calculateMetrics(tasks);
    }, [tasks]);
    const groupedByStatus = useMemo(() => {
        if (!tasks)
            return null;
        return TaskService.groupByStatus(tasks);
    }, [tasks]);
    const groupedByPriority = useMemo(() => {
        if (!tasks)
            return null;
        return TaskService.groupByPriority(tasks);
    }, [tasks]);
    const tasksNeedingAttention = useMemo(() => {
        if (!tasks)
            return [];
        return TaskService.getTasksNeedingAttention(tasks);
    }, [tasks]);
    return {
        tasks: tasks || [],
        metrics,
        groupedByStatus,
        groupedByPriority,
        tasksNeedingAttention,
        error,
        isLoading,
        refetch,
    };
}
/**
 * Helper function to invalidate tasks cache
 * Invalidates based on workspace and optional project context
 */
export function invalidateTasksCache(workspaceId, projectId) {
    // Since useServerState converts keys to JSON strings, we need to match against stringified keys
    mutate((key) => {
        if (typeof key !== "string")
            return false;
        try {
            const parsedKey = JSON.parse(key);
            if (!Array.isArray(parsedKey))
                return false;
            // Match tasks for this workspace
            if (parsedKey[0] !== "tasks" || parsedKey[1] !== workspaceId) {
                return false;
            }
            // If projectId is specified, only invalidate that project's tasks
            if (projectId) {
                return parsedKey[2] === projectId;
            }
            // Otherwise invalidate all tasks for this workspace
            return true;
        }
        catch {
            return false;
        }
    }, undefined, { revalidate: true });
}
export function useTaskActions(workspaceId, projectId) {
    const createTaskMutation = useCreateTask(workspaceId);
    const deleteTaskMutation = useDeleteTask(workspaceId);
    const createTask = async (data) => {
        try {
            const result = await createTaskMutation.trigger(data);
            // Invalidate cache after successful creation
            invalidateTasksCache(workspaceId, projectId);
            return { success: true, data: result };
        }
        catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : "Failed to create task",
            };
        }
    };
    const deleteTask = async (taskId) => {
        try {
            await deleteTaskMutation.trigger(taskId);
            // Invalidate cache after successful deletion
            invalidateTasksCache(workspaceId, projectId);
            return { success: true };
        }
        catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : "Failed to delete task",
            };
        }
    };
    return {
        createTask,
        deleteTask,
        isCreating: createTaskMutation.isLoading,
        isDeleting: deleteTaskMutation.isLoading,
    };
}
/**
 * Hook for updating a specific task
 * Must be used at component level, not inside useTaskActions
 */
export function useTaskUpdate(taskId, workspaceId, projectId) {
    const updateTaskMutation = useUpdateTask(taskId, workspaceId);
    const updateTask = async (data) => {
        try {
            const result = await updateTaskMutation.trigger(data);
            // Invalidate cache after successful update
            invalidateTasksCache(workspaceId, projectId);
            return { success: true, data: result };
        }
        catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : "Failed to update task",
            };
        }
    };
    return {
        updateTask,
        isUpdating: updateTaskMutation.isLoading,
    };
}
export function useTaskFilters(initialFilters) {
    const [filters, setFilters] = useState(initialFilters || {});
    const updateFilter = (key, value) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
        }));
    };
    const clearFilters = () => {
        setFilters({});
    };
    const hasActiveFilters = useMemo(() => {
        return Object.values(filters).some((value) => value !== undefined && value !== "");
    }, [filters]);
    return {
        filters,
        updateFilter,
        clearFilters,
        hasActiveFilters,
    };
}
export function useTaskMetrics(tasks) {
    return useMemo(() => {
        return TaskService.calculateMetrics(tasks);
    }, [tasks]);
}
